<template>
  <section class="card-section">
    <form class="card form-card" @submit.prevent="onSubmit">
      <h2 class="mt-5 text-center">
        {{ initialStudy ? 'Edit Study' : 'Register Study' }}
      </h2>

      <div
        class="form-group"
        v-for="field in [
          'identifier',
          'name',
          'studyEnrollmentPassword',
          'studyConfirmEnrollmentPassword',
        ]"
        :key="field"
      >
        <label :for="field">
          {{ labels[field] }}
        </label>
        <input
          :id="field"
          :type="types[field]"
          v-model="values[field]"
          @blur="onBlur(field)"
          @input="onInput(field)"
          :class="{ 'input-invalid': touched[field] && errors[field] }"
          :placeholder="placeholders[field]"
          :disabled="field.includes('identifier') && initialStudy ? true : false"
          :min-legth="field.includes('Password') ? 8 : null"
        />
        <p class="error" v-if="touched[field] && errors[field]">
          {{ errors[field] }}
        </p>
      </div>
      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="isLoading || !isValid">
          <loading-icon v-if="isLoading" size="16px" color="white" />
          <span v-else>
            {{ initialStudy ? 'Save Changes' : 'Register Study' }}
          </span>
        </button>
        <button
          v-if="initialStudy"
          type="button"
          class="btn btn-secondary"
          @click="$emit('cancel')"
        >
          Cancel
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useForm } from '@/common/composables/useFormValidation';

const props = defineProps({
  initialStudy: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});
const emit = defineEmits(['submit', 'cancel']);

const PASSWORD_ENROLLMENT_REGEX =
  '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\\d!@#$%^&*(),.?":{}|<>]{8,}$';

const labels = computed(() => ({
  identifier: 'Identifier',
  name: 'Name',
  studyEnrollmentPassword: props.initialStudy ? 'New Enrollment Password' : 'Enrollment Password',
  studyConfirmEnrollmentPassword: props.initialStudy ? 'Confirm New Password' : 'Confirm Password',
}));

const types = {
  identifier: 'text',
  name: 'text',
  studyEnrollmentPassword: 'password',
  studyConfirmEnrollmentPassword: 'password',
};
const placeholders = {
  identifier: 'e.g. 12345',
  name: 'e.g. Knee Study',
  studyEnrollmentPassword: '*******************',
  studyConfirmEnrollmentPassword: '*******************',
};

const validationRules = {
  identifier: (v) => (v ? '' : 'Required'),
  name: (v) => (v ? '' : 'Required'),
  studyEnrollmentPassword: (v) =>
    !v
      ? 'Required'
      : !new RegExp(PASSWORD_ENROLLMENT_REGEX).test(v)
        ? `Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character`
        : '',
  studyConfirmEnrollmentPassword: (v, vals) =>
    !v ? 'Required' : v !== vals.studyEnrollmentPassword ? 'Does not match' : '',
};

const { values, errors, touched, onBlur, onInput, isValid } = useForm(
  {
    identifier: '',
    name: '',
    studyEnrollmentPassword: '',
    studyConfirmEnrollmentPassword: '',
  },
  validationRules
);

function resetForm() {
  Object.keys(values)?.forEach((k) => (values[k] = ''));
  Object.keys(errors)?.forEach((k) => (errors[k] = ''));
  Object.keys(touched)?.forEach((k) => (touched[k] = false));
}

watch(
  () => props.initialStudy,
  (val) => {
    if (val) {
      values.identifier = val.identifier;
      values.name = val.name;
      values.studyEnrollmentPassword = '';
      values.studyConfirmEnrollmentPassword = '';
    } else {
      Object.keys(values).forEach((k) => (values[k] = ''));
    }
    Object.keys(errors).forEach((k) => (errors[k] = ''));
    Object.keys(touched).forEach((k) => (touched[k] = false));
  },
  { immediate: true }
);

async function onSubmit() {
  Object.keys(touched).forEach((f) => (touched[f] = true));
  if (!isValid.value) return;
  const payload = { ...values };
  emit('submit', payload);
  resetForm();
}
</script>

<style scoped lang="scss">
.card-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: auto;
}

.form-card {
  width: 100%;
  max-width: 750px;
}

.form-group {
  margin-bottom: 1rem;
  margin-top: 2rem;
  label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--primary-color);
    font-weight: 600;
  }
  input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 2px solid #dcdfe3;
    border-radius: 6px;
    outline: none;
    font-size: 16px;
    &:focus {
      border-color: var(--primary-color);
    }
  }
  .input-invalid {
    border-color: #e53e3e !important;
  }
  .error {
    color: #e53e3e;
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  .btn {
    padding: 0.6rem 1.2rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    &.btn-primary {
      background: var(--primary-color);
      color: #fff;
    }
    &.btn-secondary {
      background: #e0e0e0;
      color: var(--primary-color);
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
